import useEcho from "@/hooks/use-echo.hook.ts";
import { BellOn } from "@/icons/BellOn.tsx";
import { CircleCheck } from "@/icons/CircleCheck.tsx";
import api from "@/lib/api.ts";
import { NotificationModel } from "@/models/notification.model.ts";
import { getNotifications } from "@/services/notifications.service.ts";
import useUserStore from "@/store/user.store.ts";
import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
  ActionIcon,
  Text,
  Indicator,
  HoverCard,
  useMantineTheme,
  Paper,
  Button,
  ThemeIcon,
} from "@mantine/core";
import { Bell } from "@/icons/Bell";
import { Link } from "react-router";
import { CircleInfo } from "@/icons/CircleInfo";
import { CircleXMark } from "@/icons/CircleXMark";

export const NotificationsMenu = function UserMenu() {
  const theme = useMantineTheme();
  const { webSocketsUserId } = useUserStore();
  const [hasNotifications, setHasNotifications] = useState(false);
  const [notifications, setNotifications] = useState<NotificationModel[]>([]);

  const echo = useEcho();

  const { data, refetch } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => getNotifications(),
  });

  useEffect(() => {
    if (webSocketsUserId && echo) {
      echo.private(`notifications.${webSocketsUserId}`).notification(() => {
        refetch();
      });
    }
  }, [echo, webSocketsUserId]);

  useEffect(() => {
    const length = data?.data?.length || 0;
    const notificationsArray =
      data?.data.map((notification) => {
        notification.date = dayjs.utc(notification.date).fromNow();
        return notification;
      }) || [];
    setNotifications(notificationsArray);
    setHasNotifications(length > 0);
  }, [data]);

  const markAllAsRead = useMutation({
    mutationFn: () => {
      return api.post("/users/notifications/mark-all-read");
    },
    onSuccess: () => {
      refetch();
    },
  });

  return (
    <HoverCard
      width={260}
      position="bottom-end"
      transitionProps={{ transition: "pop-top-right" }}
      withinPortal
      withArrow
      arrowPosition="center"
    >
      <HoverCard.Target>
        <Indicator
          color="yellow"
          withBorder
          processing
          disabled={!hasNotifications}
        >
          <ActionIcon variant="default" size="lg">
            {!hasNotifications && <Bell />}
            {hasNotifications && (
              <BellOn style={{ color: theme.colors.yellow[7] }} />
            )}
          </ActionIcon>
        </Indicator>
      </HoverCard.Target>
      <HoverCard.Dropdown w={350} p={3}>
        <Text fw={600} size="md" p={10}>
          Notification center
        </Text>

        {!hasNotifications && (
          <Paper withBorder p="sm">
            <div className="flex gap-3">
              <ThemeIcon color="transparent">
                <CircleCheck
                  size={30}
                  style={{ color: theme.colors.green[6] }}
                />
              </ThemeIcon>

              <div className="flex flex-col gap-1">
                <Text c="dimmed" size="sm" fw={500}>
                  All caught up!
                </Text>

                <Text size="sm">No notifications found.</Text>
              </div>
            </div>
          </Paper>
        )}

        {hasNotifications && (
          <div className="flex flex-col gap-1">
            {notifications?.map((notification) => (
              <Paper key={notification.id} withBorder p="sm">
                <div className="flex gap-3">
                  <ThemeIcon color="transparent">
                    {notification.data.type === "info" && (
                      <CircleInfo
                        size={30}
                        style={{ color: theme.colors.violet[6] }}
                      />
                    )}
                    {notification.data.type === "warning" && (
                      <CircleInfo
                        size={30}
                        style={{ color: theme.colors.yellow[6] }}
                      />
                    )}
                    {notification.data.type === "error" && (
                      <CircleXMark
                        size={30}
                        style={{ color: theme.colors.red[6] }}
                      />
                    )}
                  </ThemeIcon>

                  <div className="flex flex-col gap-1">
                    <Text c="dimmed" size="sm" fw={500}>
                      {notification.data.title}
                    </Text>

                    <Text size="sm">
                      {notification.data.link && (
                        <Link to={notification.data.link}>
                          {notification.data.body}
                        </Link>
                      )}
                      {!notification.data.link && (
                        <span>{notification.data.body}</span>
                      )}
                    </Text>

                    <Text c="dimmed" size="xs">
                      {notification.date}
                    </Text>
                  </div>
                </div>
              </Paper>
            ))}

            <Button
              variant="default"
              size="sm"
              fullWidth
              loading={markAllAsRead.isPending}
              disabled={markAllAsRead.isPending}
              onClick={() => markAllAsRead.mutate()}
            >
              Mark all as read
            </Button>
          </div>
        )}
      </HoverCard.Dropdown>
    </HoverCard>
  );
};
